/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraFoundationNodeArgs,
  assert,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakMessageSessionAbortedGuard,
  HoruzProtocolChulakServiceSessionGuard,
} from '../../guards/index.js';
import {
  HoruzProtocolChulakServiceStateEvent,
  HoruzProtocolChulakServiceStateYielder,
} from '../service/enums.js';
import { HoruzError } from '../../../../errors/error.js';
import { HoruzProtocolChulakCloseCode } from '../../codes.js';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakErrorAppTimeout } from '../../errors/index.js';
import { HoruzProtocolChulakMessageModule } from '../../modules/message.js';
import { HoruzProtocolChulakServerStateBase } from './base.js';
import { HoruzProtocolChulakServerStateContext } from '../context.js';
import { HoruzProtocolChulakServerStateMap } from '../../statemap.js';
import { HoruzProtocolChulakTaskTimeoutTimer } from '../../tasks/index.js';
import { IHoruz } from '../../../../horuz.js';
const debug = debugCreate({
  namespace: 'horuz:protocols:chulak:states:server:open',
});

/**
 * Server
 *
 * Done state
 * @public
 */
export class HoruzProtocolChulakServerStateOpen extends HoruzProtocolChulakServerStateBase<
  typeof HoruzProtocolChulakServerStateMap,
  HoruzProtocolChulakServerStateContext,
  IHoruz
> {
  constructor(args: GamoraFoundationNodeArgs) {
    super(args);
  }

  /**
   * Creates underlying service context
   * @param serverStateContext
   * @returns
   */
  private async _createUnderlyingServiceContext(
    serverStateContext: HoruzProtocolChulakServerStateContext,
  ) {
    assert(
      serverStateContext.stateContextRegistry.serviceSessionUuid,
      'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
    );
    try {
      return await this.serviceSessionStore.withLock(
        serverStateContext.stateContextRegistry.serviceSessionUuid,
        async () => {
          try {
            assert(
              serverStateContext.stateContextRegistry.serverSessionUuid,
              'Invalid argument: serverSessionUuid not available in stateContextRegistry.',
            );
            assert(
              serverStateContext.stateContextRegistry.serviceSessionUuid,
              'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
            );

            // Get service session
            const serviceSession = await this.serviceSessionStore.get(
              serverStateContext.stateContextRegistry.serviceSessionUuid,
            );

            // The service session was already aborted
            if (
              serviceSession.hasDataByType(
                HoruzProtocolChulakDataType.DATA_SESSION_ABORTED,
              )
            ) {
              // Try to reconstruct the abortion error
              const abortedErrorRaw = serviceSession.getDataByType(
                HoruzProtocolChulakDataType.DATA_SESSION_ABORTED,
              );
              const abortedError =
                HoruzProtocolChulakMessageModule.Decode<HoruzProtocolChulakMessageSessionAbortedGuard>(
                  abortedErrorRaw,
                );

              // Set last error
              this._trySetLastError(serverStateContext, {
                error: new HoruzError(abortedError.code, abortedError.reason),
                forceError: true,
              });

              this.$postTransition(serverStateContext, { stateNext: 'Close' });
              return;
            }

            // Create service context
            const serviceContextIdentifier =
              this.serviceController.$createContext({
                defaultStateAbort: 'Abort',
                defaultStateStart: serviceSession.lastState ?? 'AgentStart',
                contextIdentifier: serverStateContext.contextIdentifier,
              });
            const serviceControllerContext = this.serviceController.$getContext(
              serviceContextIdentifier,
            );
            const serverControllerContext = this.$controller.$getContext(
              serverStateContext.contextIdentifier,
            );

            // Set context on service session
            serviceControllerContext.stateContext.stateContextRegistry =
              this.serviceController.createContextRegistry(
                serverStateContext.stateContextRegistry.serverSessionUuid,
                serverStateContext.stateContextRegistry.serviceSessionUuid,
              );

            // Transfer websocket to service controller
            serviceControllerContext.stateContext.ws = serverStateContext.ws;

            // Listen for abort or done handlers
            this.serviceController
              .$('Abort')
              .on(
                serviceContextIdentifier,
                HoruzProtocolChulakServiceStateEvent.Aborted,
                async (_, input) => {
                  this.$tryPostTask(serverStateContext, async () => {
                    // Set abort error
                    this._trySetLastError(serverStateContext, {
                      error: input.error,
                      forceError: true,
                    });

                    // Post transition to close
                    this.$tryPostTransition(serverStateContext, {
                      stateNext: 'Close',
                    });
                  });
                },
              );

            // Listen for abort or done handlers
            this.serviceController
              .$('Done')
              .on(
                serviceContextIdentifier,
                HoruzProtocolChulakServiceStateEvent.Done,
                async (_) => {
                  this.$tryPostTask(serverStateContext, async () => {
                    // Everything went well and go to close
                    this.$tryPostTransition(serverStateContext, {
                      stateNext: 'Close',
                    });
                  });
                },
              );

            // Start timeout timer on service session
            await this._startTimeoutTimerTask(serviceSession);

            // Set active context controller for service
            serviceSession.addContextIdentifier(serviceContextIdentifier);

            // Save service session
            await this.serviceSessionStore.upsert(serviceSession);

            // Delay to next iteration
            this.serviceController.$startContext(serviceContextIdentifier, {});
          } catch (e: any) {
            debug.log('($enter): Unable to get create serviceSession. ');
            debug.trace(e?.message, e?.stack);
            this._tryHandleException(serverStateContext, e);
          }
        },
      );
    } catch (e: any) {
      debug.log('($enter): Unable to get serviceSession lock. ');
      debug.trace(e?.message, e?.stack);
      this._tryHandleException(serverStateContext, e);
    }
  }

  /**
   * Stops underlying service context
   * @param serverStateContext The server context
   * @returns
   */
  private async _endUnderlyingServiceContext(
    serverStateContext: HoruzProtocolChulakServerStateContext,
  ) {
    assert(
      serverStateContext.stateContextRegistry.serviceSessionUuid,
      'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
    );
    try {
      return await this.serviceSessionStore.withLock(
        serverStateContext.stateContextRegistry.serviceSessionUuid,
        async () => {
          try {
            assert(
              serverStateContext.stateContextRegistry.serverSessionUuid,
              'Invalid argument: serverSessionUuid not available in stateContextRegistry.',
            );
            assert(
              serverStateContext.stateContextRegistry.serviceSessionUuid,
              'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
            );

            // Get service session
            const serviceSession = await this.serviceSessionStore.get(
              serverStateContext.stateContextRegistry.serviceSessionUuid,
            );

            // Stop timer
            await this._stopTimeoutTimerTask(serviceSession);

            // Remove context identifiers
            serviceSession.contextIdentifiers =
              serviceSession.contextIdentifiers.filter((contextIdentifier) => {
                return (
                  contextIdentifier !== serverStateContext.contextIdentifier
                );
              });

            // Save service session
            await this.serviceSessionStore.upsert(serviceSession);

            const isAgentCancel =
              serverStateContext.stateContextRegistry.lastError?.code ==
              HoruzProtocolChulakCloseCode.AGENT_CANCEL;

            if (isAgentCancel) {
              // Update lastError in the service controller, so that the error
              // is propagated to the service implementing Horuz (wallet).
              this.serviceController.$getContext(
                serverStateContext.contextIdentifier,
              ).stateContext.stateContextRegistry.lastError =
                serverStateContext.stateContextRegistry.lastError;
            }

            // Abort underlying context if exists
            this.serviceController
              .$tryAbortContext(serverStateContext.contextIdentifier, {
                stateAbort: isAgentCancel ? 'Abort' : 'Stopped',
              })
              ?.catch(() => {
                debug.log(
                  '(_endUnderlyingServiceContext): Unable to stop underlying context.',
                );
              });
          } catch (e: any) {
            debug.log(
              '($_endUnderlyingServiceContext): Unable to get close context identifier for serviceSession. ',
            );
            debug.trace(e?.message, e?.stack);
          }
        },
      );
    } catch (e: any) {
      debug.log('($enter): Unable to get serviceSession lock. ');
      debug.trace(e?.message, e?.stack);
    }
  }

  /**
   * Initialize timers
   * @param stateContext
   */
  private _startTimeoutTimerTask = async (
    serviceSession: HoruzProtocolChulakServiceSessionGuard,
  ) => {
    const taskManager = this.$root
      .$('protocols')
      .$('chulak')
      .$('tasks')
      .$('manager');

    // Task is already running
    if (taskManager.$hasTask(serviceSession.uuid.toJSON(), 'timeoutTimer')) {
      return;
    }

    // Create authorization timer
    const timeoutTimerTask = new HoruzProtocolChulakTaskTimeoutTimer({
      namespace: serviceSession.uuid.toJSON(),
      name: 'timeoutTimer',
      callback: async () => {
        // Send abort to service
        await this.serviceController
          .$('Abort')
          .yielder(
            serviceSession.uuid,
            HoruzProtocolChulakServiceStateYielder.PersistSessionAbortMessage,
            {
              error: HoruzProtocolChulakErrorAppTimeout.Create('Timeout'),
            },
          );

        // Notify the abort
        await this.serviceController
          .$('Abort')
          .yielder(
            serviceSession.uuid,
            HoruzProtocolChulakServiceStateYielder.BroadcastSessionAbortToClients,
          );

        // Unregister task
        taskManager.$unregisterTask(timeoutTimerTask);
      },
      ms: serviceSession.exp * 1000,
    });

    // Start task
    await timeoutTimerTask.$start();

    // Register task in manager
    taskManager.$registerTask(timeoutTimerTask);
  };

  /**
   * Stop timers
   * @param stateContext
   */
  private _stopTimeoutTimerTask = async (
    serviceSession: HoruzProtocolChulakServiceSessionGuard,
  ) => {
    const taskManager = this.$root
      .$('protocols')
      .$('chulak')
      .$('tasks')
      .$('manager');

    // Already stopped
    if (!taskManager.$hasTask(serviceSession.uuid.toJSON(), 'timeoutTimer')) {
      return;
    }

    const timeoutTimerTask = taskManager.$getTask(
      serviceSession.uuid.toJSON(),
      'timeoutTimer',
    );

    // Stop and unregister task
    await timeoutTimerTask.$terminate();
    taskManager.$unregisterTask(timeoutTimerTask);
  };

  /**
   * Enter state
   */
  override async $enter(
    serverStateContext: HoruzProtocolChulakServerStateContext,
  ): Promise<void> {
    return await this._createUnderlyingServiceContext(serverStateContext);
  }

  /**
   * Exit state
   */
  override async $exit(
    serverStateContext: HoruzProtocolChulakServerStateContext,
  ): Promise<void> {
    assert(
      serverStateContext.stateContextRegistry.serviceSessionUuid,
      'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
    );
    return await this._endUnderlyingServiceContext(serverStateContext);
  }
}
